import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ConfirmDialog from "components/common/ConfirmDialog";
import CustomButton from "components/common/CustomButton";
import { useEffect, useState } from "react";

type LiveView = {
  task_id: number;
  status: string;
  phase?: string;
  instruction?: string;
  live_view_url?: string | null;
  error_message?: string;
};

type Props = {
  open: boolean;
  liveView: LiveView | null;
  taskLabel: string;
  canControl: boolean;
  releasing: boolean;
  onRelease: () => void;
  onClose: () => void;
};

/**
 * The operator's window into a running bot's browser.
 *
 * Sends nothing back: the operator moves the real pointer over VNC and the bot
 * notices on its own that the challenge is behind it. Giving up is the only
 * command, and it costs the login attempt.
 */
export default function CaptchaLiveViewDialog({
  open,
  liveView,
  taskLabel,
  canControl,
  releasing,
  onRelease,
  onClose,
}: Props) {
  const [confirmRelease, setConfirmRelease] = useState(false);
  // Held across polls so a poll without a URL cannot blank the iframe and drop
  // the operator's VNC session. A genuinely new URL is followed: a new session
  // on the same task retires the old token.
  const [liveUrl, setLiveUrl] = useState<string | null>(null);

  const url = liveView?.live_view_url ?? null;
  useEffect(() => {
    if (!open) {
      setLiveUrl(null);
      return;
    }
    if (url && url !== liveUrl) setLiveUrl(url);
  }, [open, url, liveUrl]);

  const waitingForCaptcha = (liveView?.phase || "").toUpperCase() !== "CAPTCHA";

  return (
    <>
      <Dialog fullScreen open={open} onClose={() => undefined}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
            Live browser - {taskLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {waitingForCaptcha
              ? "Watching the bot log in. Do not click unless it is stuck."
              : liveView?.instruction ||
                "Solve the captcha here; the bot continues by itself."}
          </Typography>
          {canControl && (
            <CustomButton
              fullWidth={false}
              disabled={releasing}
              onClick={() => setConfirmRelease(true)}
            >
              Give up on this challenge
            </CustomButton>
          )}
          <Tooltip title="Hide (the bot keeps running)">
            <IconButton onClick={onClose} aria-label="Hide live view">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1, position: "relative", bgcolor: "black" }}>
          {liveUrl ? (
            <iframe
              title="Bot browser live view"
              src={liveUrl}
              style={{
                border: 0,
                width: "100%",
                height: "100%",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                color: "common.white",
              }}
            >
              <CircularProgress size={24} color="inherit" />
              <Typography>Connecting to the bot's browser...</Typography>
            </Box>
          )}
          {liveView?.error_message ? (
            <Alert
              severity="error"
              sx={{ position: "absolute", inset: "auto 16px 16px" }}
            >
              {liveView.error_message}
            </Alert>
          ) : null}
        </Box>
      </Dialog>

      <ConfirmDialog
        open={confirmRelease}
        title="Give up on this challenge"
        message="The bot stops waiting and starts the login over, which means a fresh 2FA code. Usually it is quicker to ask hCaptcha for another puzzle with its own reload button."
        confirmLabel="Give up"
        cancelLabel="Keep trying"
        loading={releasing}
        onConfirm={() => {
          setConfirmRelease(false);
          onRelease();
        }}
        onCancel={() => setConfirmRelease(false)}
      />
    </>
  );
}
